import { useState, useEffect } from 'react';
import { ToolLayout } from '../../components/tools/ToolLayout';
import { SUPPORTED_COINS } from '../../data/coins';

type Currency = 'brl' | (typeof SUPPORTED_COINS)[number]['id'];

export const ConversorPage: React.FC = () => {
  const [amount, setAmount] = useState('1');
  const [from, setFrom] = useState<Currency>('bitcoin');
  const [to, setTo] = useState<Currency>('brl');
  const [pricesBrl, setPricesBrl] = useState<Record<string, number>>({});
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchPrices = async () => {
    try {
      const ids = SUPPORTED_COINS.map((c) => c.id).join(',');
      const res = await fetch(
        `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=brl`
      );
      const data = await res.json();
      const next: Record<string, number> = {};
      for (const coin of SUPPORTED_COINS) {
        if (data?.[coin.id]?.brl) next[coin.id] = data[coin.id].brl;
      }
      if (Object.keys(next).length === 0) throw new Error('sem dados');
      setPricesBrl(next);
      setUpdatedAt(new Date());
      setError(null);
    } catch {
      setError('Não foi possível obter as cotações agora. Tente novamente em instantes.');
    }
  };

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 60_000);
    return () => clearInterval(interval);
  }, []);

  const priceOf = (currency: Currency): number | undefined =>
    currency === 'brl' ? 1 : pricesBrl[currency];

  const amountNum = parseFloat(amount.replace(',', '.'));
  const fromPrice = priceOf(from);
  const toPrice = priceOf(to);
  const converted =
    Number.isFinite(amountNum) && amountNum >= 0 && fromPrice && toPrice
      ? (amountNum * fromPrice) / toPrice
      : null;

  const labelOf = (currency: Currency): string =>
    currency === 'brl'
      ? 'Real (BRL)'
      : (() => {
          const coin = SUPPORTED_COINS.find((c) => c.id === currency)!;
          return `${coin.name} (${coin.symbol})`;
        })();

  const formatResult = (value: number, currency: Currency): string =>
    currency === 'brl'
      ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 })
      : `${value.toLocaleString('pt-BR', { maximumFractionDigits: 8 })} ${SUPPORTED_COINS.find((c) => c.id === currency)!.symbol}`;

  const selectClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500';

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  return (
    <ToolLayout
      path="/ferramentas/conversor"
      title="Conversor de Criptomoedas"
      metaTitle="Conversor de Criptomoedas para Real (BRL)"
      metaDescription="Converta Bitcoin, Ethereum e outras criptomoedas para reais — e entre criptomoedas — com cotação atualizada e fonte identificada."
      intro={[
        'Converta valores entre reais e as principais criptomoedas, ou de uma criptomoeda para outra, usando cotações de mercado atualizadas a cada minuto.',
        'A conversão entre duas criptomoedas usa o real como moeda-ponte: o valor é convertido do ativo de origem para BRL e, em seguida, para o ativo de destino.',
      ]}
      methodology={[
        'Cotações em BRL fornecidas pela API pública da CoinGecko, atualizadas a cada 60 segundos.',
        'Conversão cripto → cripto calculada via BRL (moeda-ponte).',
        'Os valores não incluem spread ou taxas de exchange — o preço efetivo de negociação pode variar.',
      ]}
      faqs={[
        {
          question: 'A cotação é a mesma da minha exchange?',
          answer:
            'Não exatamente. A CoinGecko calcula um preço médio global de mercado. Cada exchange tem seu próprio livro de ofertas, então o preço efetivo pode variar alguns décimos por cento — além de taxas e spread.',
        },
        {
          question: 'Com que frequência os preços são atualizados?',
          answer: 'A cada 60 segundos enquanto a página está aberta. O horário da última atualização é exibido junto ao resultado.',
        },
      ]}
    >
      <div className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_1fr] gap-4 items-end">
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Quantidade</span>
            <input
              type="number"
              min="0"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={selectClass}
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">De</span>
            <select value={from} onChange={(e) => setFrom(e.target.value as Currency)} className={selectClass}>
              <option value="brl">Real (BRL)</option>
              {SUPPORTED_COINS.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.symbol})</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={swap}
            aria-label="Inverter moedas"
            className="border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 transition text-gray-600"
          >
            ⇄
          </button>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Para</span>
            <select value={to} onChange={(e) => setTo(e.target.value as Currency)} className={selectClass}>
              <option value="brl">Real (BRL)</option>
              {SUPPORTED_COINS.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.symbol})</option>
              ))}
            </select>
          </label>
        </div>

        {error && (
          <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</p>
        )}

        {converted !== null && !error && (
          <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-6 text-center">
            <p className="text-sm text-gray-600 mb-1">
              {Number.isFinite(amountNum) ? amountNum.toLocaleString('pt-BR', { maximumFractionDigits: 8 }) : 0}{' '}
              {labelOf(from)} =
            </p>
            <p className="text-2xl md:text-3xl font-bold text-blue-800">{formatResult(converted, to)}</p>
            {updatedAt && (
              <p className="text-xs text-gray-400 mt-3">
                Fonte: CoinGecko · atualizado às {updatedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        )}
      </div>
    </ToolLayout>
  );
};
